import React from 'react';
import { SearchField, numberStyle } from './controls';
import { DIM, DIVIDER, INK, MONO, MUTED, ROW_ERROR, TEXT, TINT, card } from './tokens';

/** The lines card: toolbar (title + count, find in this document, actions), the lines, then the totals. */
export const LinesCard: React.FC<{
  phone: boolean;
  titleId: string;
  title: string;
  /** Left out while the lines load. */
  count?: number;
  search?: { label: string; placeholder: string; value: string; onChange: (value: string) => void };
  actions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ phone, titleId, title, count, search, actions, children, footer }) => (
  <section aria-labelledby={titleId} style={{ ...card, overflow: 'hidden' }}>
    <div
      style={
        phone
          ? { display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px 16px', borderBottom: `1px solid ${DIVIDER}` }
          : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: `1px solid ${DIVIDER}` }
      }
    >
      <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', minWidth: 0 }}>
        <h2 id={titleId} style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: INK }}>
          {title} {count !== undefined && <span style={{ fontWeight: 500, color: DIM, fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
        </h2>
      </div>
      {search && <SearchField phone={phone} width={phone ? undefined : '300px'} {...search} />}
      {actions && <div style={phone ? { display: 'flex', gap: '8px' } : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>{actions}</div>}
    </div>
    {children}
    {footer}
  </section>
);

export interface TotalRow {
  label: string;
  value: string;
  strong?: boolean;
}

/** Subtotal / VAT / Total, right-aligned; `aside` sits on the left of the same row (desktop). */
export const TotalsList: React.FC<{ phone: boolean; rows: TotalRow[]; aside?: React.ReactNode }> = ({ phone, rows, aside }) => {
  const list = (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: phone ? 'minmax(0, 1fr) auto' : 'auto minmax(120px, max-content)',
        justifyContent: phone ? undefined : 'end',
        gap: phone ? '6px 16px' : '6px 32px',
        padding: phone ? '14px 16px 16px' : aside ? 0 : '16px 20px 20px',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <dt style={{ fontSize: '14px', lineHeight: row.strong ? '28px' : '20px', fontWeight: row.strong ? 600 : 400, color: row.strong ? INK : MUTED }}>{row.label}</dt>
          <dd style={{ fontSize: row.strong ? '20px' : '14px', lineHeight: row.strong ? '28px' : '20px', fontWeight: row.strong ? 600 : 400, color: row.strong ? INK : TEXT, textAlign: 'right' }}>
            {row.value}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
  if (!aside || phone) return list;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px', padding: '16px 20px 20px' }}>
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>{aside}</div>
      {list}
    </div>
  );
};

/** Header cell; `width` fixes a value column (Product takes the rest). */
export const th = (align: 'left' | 'right', options: { width?: number; first?: boolean; last?: boolean; padRight?: number; padLeft?: number } = {}): React.CSSProperties => ({
  width: options.width ? `${options.width}px` : undefined,
  height: '44px',
  padding: `0 ${options.padRight ?? (options.last ? 20 : 6)}px 0 ${options.padLeft ?? (options.first ? 20 : 6)}px`,
  background: TINT,
  borderBottom: `1px solid ${DIVIDER}`,
  textAlign: align,
  fontSize: '12.5px',
  lineHeight: '18px',
  fontWeight: 600,
  color: MUTED,
  whiteSpace: 'nowrap',
});

/** Body cell: 12px above and below the 40px inputs, so line rows are 64px. */
export const td = (options: { align?: 'left' | 'right'; first?: boolean; last?: boolean; padRight?: number; padLeft?: number; padTop?: number; error?: boolean } = {}): React.CSSProperties => ({
  padding: `${options.padTop ?? 12}px ${options.padRight ?? (options.last ? 20 : 6)}px 12px ${options.padLeft ?? (options.first ? 20 : 6)}px`,
  borderBottom: `1px solid ${DIVIDER}`,
  verticalAlign: 'top',
  textAlign: options.align ?? 'left',
  background: options.error ? ROW_ERROR : undefined,
});

export const tableStyle = (minWidth: number): React.CSSProperties => ({
  width: '100%',
  minWidth: `${minWidth}px`,
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
  fontSize: '14px',
  lineHeight: '20px',
  color: TEXT,
});

/** Name alone on line 1; "SKU · context" on line 2. */
export const ProductCell: React.FC<{ name: string; sku?: string | null; context?: string; muted?: boolean; phone?: boolean; nameId?: string }> = ({ name, sku, context, muted, phone, nameId }) => (
  <div style={{ minHeight: phone ? undefined : '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px', minWidth: 0, flex: phone ? '1 1 auto' : undefined }}>
    <span
      id={nameId}
      style={{
        minWidth: 0,
        fontSize: phone ? '15px' : '14px',
        lineHeight: phone ? '22px' : '20px',
        fontWeight: 500,
        color: muted ? MUTED : TEXT,
        whiteSpace: phone ? undefined : 'nowrap',
        overflow: phone ? undefined : 'hidden',
        textOverflow: phone ? undefined : 'ellipsis',
      }}
    >
      {name}
    </span>
    <span style={{ fontSize: '12.5px', lineHeight: '18px', color: MUTED, whiteSpace: phone ? undefined : 'nowrap', overflow: phone ? undefined : 'hidden', textOverflow: phone ? undefined : 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>
      {sku && <span style={{ fontFamily: MONO, fontSize: '12px' }}>{sku}</span>}
      {sku && context ? ' · ' : ''}
      {context}
    </span>
  </div>
);

/** A read-only value in a 40px (desktop) or 44px (phone) line box, so it lines up with the inputs. */
export const ReadValue: React.FC<{ phone?: boolean; strong?: boolean; muted?: boolean; children: React.ReactNode }> = ({ phone, strong, muted, children }) => (
  <div style={{ lineHeight: phone ? '44px' : '40px', fontSize: phone ? '15px' : '14px', fontWeight: strong ? (phone ? 600 : 500) : 400, color: muted ? MUTED : strong ? INK : TEXT, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
    {children}
  </div>
);

/** One phone line block; tinted when the line needs attention. */
export const PhoneLine: React.FC<{ labelledBy: string; error?: boolean; children: React.ReactNode }> = ({ labelledBy, error, children }) => (
  <div role="group" aria-labelledby={labelledBy} style={{ padding: '14px 16px', borderBottom: `1px solid ${DIVIDER}`, background: error ? ROW_ERROR : undefined }}>
    {children}
  </div>
);

export const PhoneLabel: React.FC<{ htmlFor?: string; align?: 'left' | 'right'; children: React.ReactNode }> = ({ htmlFor, align = 'left', children }) =>
  htmlFor ? (
    <label htmlFor={htmlFor} style={{ display: 'block', marginBottom: '4px', fontSize: '12.5px', lineHeight: '18px', fontWeight: 500, color: MUTED, textAlign: align }}>
      {children}
    </label>
  ) : (
    <p style={{ marginBottom: '4px', fontSize: '12.5px', lineHeight: '18px', fontWeight: 500, color: MUTED, textAlign: align }}>{children}</p>
  );

/** Enter moves to the same field on the next line, so a delivery can be counted top to bottom. */
const focusSameFieldBelow = (input: HTMLInputElement) => {
  const scope = input.closest('[data-lines]') ?? document;
  const fields = Array.from(scope.querySelectorAll<HTMLInputElement>(`input[data-col="${input.dataset.col}"]`)).filter((el) => !el.disabled && el.getClientRects().length > 0);
  const next = fields[fields.indexOf(input) + 1];
  if (next) {
    next.focus();
    next.select();
  } else input.blur();
};

/** A right-aligned number input (quantity, or a price with the € prefix). */
export const NumberField: React.FC<{
  id: string;
  column: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Hidden label for desktop cells; phone blocks render a visible PhoneLabel instead. */
  srLabel?: string;
  invalid?: boolean;
  describedBy?: string;
  phone?: boolean;
  prefix?: string;
  disabled?: boolean;
}> = ({ id, column, value, onChange, onBlur, srLabel, invalid, describedBy, phone, prefix, disabled }) => (
  <div style={{ position: 'relative' }}>
    {srLabel && (
      <label className="pu-sr" htmlFor={id}>
        {srLabel}
      </label>
    )}
    {prefix && (
      <span aria-hidden="true" style={{ position: 'absolute', left: '10px', top: phone ? '11px' : '10px', fontSize: phone ? '16px' : '14px', lineHeight: phone ? '22px' : '20px', color: MUTED, pointerEvents: 'none' }}>
        {prefix}
      </span>
    )}
    <input
      id={id}
      data-col={column}
      className="pu-input"
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={value}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        focusSameFieldBelow(event.currentTarget);
      }}
      style={{ ...numberStyle(phone, invalid), paddingLeft: prefix ? '24px' : undefined }}
    />
  </div>
);
